// Lightweight generic CRUD page for commercial entities.
// Used for: clients, brands, agencies, contacts, categories, products, talents.
// Each entity declares: { title, endpoint, columns, fields }.
// Field types: text, textarea, select, number, switch, currency.
import { useEffect, useState, useMemo } from "react";
import { Plus, Edit3, Trash2, Copy, Save, X, Search } from "lucide-react";
import { api, formatErr } from "@/lib/api";

export default function CommercialEntity({ schema, lookups = {} }) {
  const { title, subtitle, endpoint, columns, fields, defaults = {}, filters = [] } = schema;
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [activeFilters, setActiveFilters] = useState({});

  const load = () => api.get(`/admin/${endpoint}`).then(setItems).catch((e) => setError(formatErr(e.response?.data?.detail) || "Error"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const filtered = useMemo(() => {
    let arr = items;
    if (q.trim()) {
      const Q = q.toLowerCase();
      arr = arr.filter((it) => JSON.stringify(it).toLowerCase().includes(Q));
    }
    Object.entries(activeFilters).forEach(([k, v]) => {
      if (!v) return;
      if (k === "_created_after") {
        arr = arr.filter((it) => (it.created_at || "") >= v);
      } else if (k === "_created_before") {
        arr = arr.filter((it) => (it.created_at || "") <= v + "T23:59:59");
      } else {
        arr = arr.filter((it) => String(it[k] || "") === String(v));
      }
    });
    return arr;
  }, [q, items, activeFilters]);

  const save = async () => {
    setError("");
    try {
      const body = { ...editing };
      Object.keys(body).forEach((k) => { if (body[k] === undefined) delete body[k]; });
      if (editing.id && items.find((x) => x.id === editing.id)) {
        await api.put(`/admin/${endpoint}/${editing.id}`, body);
      } else {
        delete body.id;
        await api.post(`/admin/${endpoint}`, body);
      }
      setEditing(null); load();
    } catch (e) { setError(formatErr(e.response?.data?.detail) || "Error al guardar"); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    try { await api.delete(`/admin/${endpoint}/${id}`); load(); }
    catch (e) { setError(formatErr(e.response?.data?.detail) || "Error"); }
  };

  const dup = async (id) => {
    try { await api.post(`/admin/${endpoint}/${id}/duplicate`, {}); load(); }
    catch (e) { setError(formatErr(e.response?.data?.detail) || "Error"); }
  };

  return (
    <div data-testid={`commercial-${endpoint.replace(/\//g, "-")}`}>
      <div className="flex items-end justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter mb-1">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle} · Total: {items.length}</p>}
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..." className="h-10 pl-9 pr-3 bg-background border border-border rounded-sm text-sm w-56 focus:border-primary outline-none" data-testid="entity-search" />
          </div>
          <button onClick={() => setEditing({ ...defaults })} data-testid="entity-new" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-sm font-semibold text-sm">
            <Plus className="w-4 h-4" /> Nuevo
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-end" data-testid="entity-date-filters">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Creado desde</label>
          <input type="date" value={activeFilters._created_after || ""} onChange={(e) => setActiveFilters({ ...activeFilters, _created_after: e.target.value })} className="h-9 px-2 bg-background border border-border rounded-sm text-sm" data-testid="entity-filter-created-after" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Creado hasta</label>
          <input type="date" value={activeFilters._created_before || ""} onChange={(e) => setActiveFilters({ ...activeFilters, _created_before: e.target.value })} className="h-9 px-2 bg-background border border-border rounded-sm text-sm" data-testid="entity-filter-created-before" />
        </div>
        {(activeFilters._created_after || activeFilters._created_before) && (
          <button onClick={() => setActiveFilters({ ...activeFilters, _created_after: "", _created_before: "" })} className="h-9 px-3 border border-border rounded-sm text-xs font-semibold hover:border-primary self-end">Limpiar fechas</button>
        )}
      </div>

      {filters.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4 items-end" data-testid="entity-filters">
          {filters.map((flt) => {
            const opts = typeof flt.options === "function" ? flt.options(lookups) : (flt.options || []);
            return (
              <div key={flt.key}>
                <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">{flt.label}</label>
                <select
                  value={activeFilters[flt.key] || ""}
                  onChange={(e) => setActiveFilters({ ...activeFilters, [flt.key]: e.target.value })}
                  data-testid={`entity-filter-${flt.key}`}
                  className="h-9 px-2 bg-background border border-border rounded-sm text-sm min-w-[140px]"
                >
                  <option value="">Todos</option>
                  {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            );
          })}
          {Object.values(activeFilters).some((v) => v) && (
            <button onClick={() => setActiveFilters({})} className="h-9 px-3 border border-border rounded-sm text-xs font-semibold hover:border-primary self-end" data-testid="entity-filter-clear">Limpiar</button>
          )}
        </div>
      )}

      {editing && (
        <div className="border border-primary rounded-sm p-6 mb-6 bg-secondary/10" data-testid="entity-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-xl">{editing.id ? "Editar" : "Nuevo"}</h3>
            <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {fields.map((f) => (
              <FieldRender key={f.key} f={f} value={editing[f.key]} onChange={(v) => setEditing({ ...editing, [f.key]: v })} lookups={lookups} />
            ))}
          </div>
          {error && <p className="text-sm text-destructive mt-3">{error}</p>}
          <div className="mt-5 flex gap-2">
            <button onClick={save} data-testid="entity-save" className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-sm text-sm font-semibold"><Save className="w-4 h-4" /> Guardar</button>
            <button onClick={() => setEditing(null)} className="px-5 py-2 border border-border rounded-sm text-sm">Cancelar</button>
          </div>
        </div>
      )}

      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-widest">
            <tr>
              {columns.map((c) => <th key={c.key} className="text-left p-3">{c.label}</th>)}
              <th className="text-left p-3">Creado</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => (
              <tr key={it.id} className="border-t border-border hover:bg-secondary/30">
                {columns.map((c) => (
                  <td key={c.key} className="p-3">
                    {c.render ? c.render(it, lookups) : (it[c.key] ?? "—")}
                  </td>
                ))}
                <td className="p-3 text-xs text-muted-foreground" title={it.updated_at ? `Actualizado ${it.updated_at.replace("T"," ").slice(0,16)}` : ""}>{(it.created_at || "").replace("T"," ").slice(0, 16) || "—"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing({ ...it })} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold mr-2 hover:border-primary"><Edit3 className="w-3 h-3" /> Editar</button>
                  <button onClick={() => dup(it.id)} className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm text-xs font-semibold mr-2 hover:border-primary"><Copy className="w-3 h-3" /> Duplicar</button>
                  <button onClick={() => del(it.id)} className="inline-flex items-center gap-1 px-3 py-1.5 border border-destructive/40 text-destructive rounded-sm text-xs font-semibold hover:bg-destructive/10"><Trash2 className="w-3 h-3" /> Eliminar</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={columns.length + 2} className="p-6 text-center text-muted-foreground">Sin registros</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FieldRender({ f, value, onChange, lookups }) {
  const colSpan = f.span === 2 ? "md:col-span-2" : "";
  if (f.type === "textarea") {
    return (
      <div className={colSpan}>
        <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">{f.label}</label>
        <textarea rows={f.rows || 3} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none resize-none" />
      </div>
    );
  }
  if (f.type === "select") {
    const opts = typeof f.options === "function" ? f.options(lookups) : (f.options || []);
    return (
      <div className={colSpan}>
        <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">{f.label}</label>
        <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none">
          {f.allowEmpty && <option value="">— Ninguno —</option>}
          {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    );
  }
  if (f.type === "switch") {
    return (
      <label className={`flex items-center gap-2 h-10 mt-6 ${colSpan}`}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="accent-primary" />
        <span className="text-sm">{f.label}</span>
      </label>
    );
  }
  if (f.type === "number") {
    return (
      <div className={colSpan}>
        <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">{f.label}</label>
        <input type="number" step={f.step || "0.01"} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
      </div>
    );
  }
  if (f.type === "currency") {
    return (
      <div className={colSpan}>
        <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">{f.label}</label>
        <select value={value || "BOB"} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none">
          <option value="BOB">BOB · Boliviano</option>
          <option value="USD">USD · Dólar</option>
        </select>
      </div>
    );
  }
  return (
    <div className={colSpan}>
      <label className="text-xs uppercase tracking-widest font-bold mb-1.5 block">{f.label}</label>
      <input type={f.type || "text"} value={value || ""} placeholder={f.placeholder} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none" />
    </div>
  );
}
